import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Calendar,
  Bell,
  Download,
  RefreshCw,
  Settings,
  Mail,
  Clock,
  TrendingUp,
  Home,
  Zap,
  CheckCircle,
  AlertCircle,
  Play,
  Pause
} from "lucide-react";

export default function Automation() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Rebalance Schedule State
  const [rebalanceConfig, setRebalanceConfig] = useState({
    enabled: false,
    frequency: "weekly",
    day_of_week: 0,
    day_of_month: 1,
    hour: 9,
    target_allocations: { stocks: 60, crypto: 20, bonds: 20 },
    max_trade_amount: 1000,
    notify_on_execution: true
  });
  const [rebalanceStatus, setRebalanceStatus] = useState(null);
  const [rebalanceHistory, setRebalanceHistory] = useState([]);
  
  // Notification Settings State
  const [notifSettings, setNotifSettings] = useState({
    enabled: false,
    email_address: "",
    trade_alerts: true,
    price_alerts: true,
    rebalance_alerts: true,
    daily_summary: false,
    price_threshold_pct: 5.0
  });
  
  // Research History State
  const [researchHistory, setResearchHistory] = useState([]);
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [rebalanceRes, notifRes, researchRes] = await Promise.all([
        api.get("/rebalance/schedule/status"),
        api.get("/notifications/settings"),
        api.get("/ai/real-estate/research-history")
      ]);
      
      if (rebalanceRes.data.config) {
        setRebalanceConfig(prev => ({ ...prev, ...rebalanceRes.data.config }));
      }
      setRebalanceStatus(rebalanceRes.data);
      setRebalanceHistory(rebalanceRes.data.history || []);
      setNotifSettings(prev => ({ ...prev, ...notifRes.data }));
      setResearchHistory(researchRes.data || []);
    } catch (e) {
      console.error("Failed to fetch automation data:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveRebalanceConfig = async () => {
    setSaving(true);
    try {
      await api.post("/rebalance/schedule/configure", rebalanceConfig);
      toast.success("Rebalancing schedule saved!");
      fetchAllData();
    } catch (e) {
      toast.error("Failed to save rebalancing config");
    } finally {
      setSaving(false);
    }
  };

  const executeRebalanceNow = async () => {
    toast.info("Executing rebalancing...");
    try {
      await api.post("/rebalance/execute-now");
      toast.success("Rebalancing executed!");
      fetchAllData();
    } catch (e) {
      toast.error("Failed to execute rebalancing");
    }
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await api.post("/notifications/settings", notifSettings);
      toast.success("Notification settings saved!");
    } catch (e) {
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      const res = await api.post("/notifications/test");
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test notification");
    }
  };

  const downloadBulkReports = async () => {
    toast.info("Generating reports ZIP...");
    try {
      const response = await api.get("/reports/bulk-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `WealthHub_Reports_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Reports downloaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download reports");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
            Automation & Settings
          </h1>
          <p className="text-zinc-500 mt-1">Configure automated trading, notifications, and exports</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scheduled Rebalancing Card */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Calendar className="w-5 h-5 text-violet-400" />
                Scheduled Portfolio Rebalancing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Auto-Rebalancing</Label>
                  <p className="text-xs text-zinc-500">Automatically rebalance your portfolio on schedule</p>
                </div>
                <Switch
                  checked={rebalanceConfig.enabled}
                  onCheckedChange={(checked) => setRebalanceConfig(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Frequency</Label>
                  <select
                    value={rebalanceConfig.frequency}
                    onChange={(e) => setRebalanceConfig(prev => ({ ...prev, frequency: e.target.value }))}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-white"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Run at (UTC)</Label>
                  <select
                    value={rebalanceConfig.hour}
                    onChange={(e) => setRebalanceConfig(prev => ({ ...prev, hour: parseInt(e.target.value) }))}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-white"
                  >
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
              </div>

              {rebalanceConfig.frequency === "weekly" && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Day of Week</Label>
                  <select
                    value={rebalanceConfig.day_of_week}
                    onChange={(e) => setRebalanceConfig(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                    className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2 text-white"
                  >
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day, i) => (
                      <option key={i} value={i}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-zinc-300">Max Trade Amount ($)</Label>
                <Input
                  type="number"
                  value={rebalanceConfig.max_trade_amount}
                  onChange={(e) => setRebalanceConfig(prev => ({ ...prev, max_trade_amount: parseFloat(e.target.value) || 0 }))}
                  className="bg-black/50 border-white/10 rounded-sm"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Email on Execution</Label>
                <Switch
                  checked={rebalanceConfig.notify_on_execution}
                  onCheckedChange={(checked) => setRebalanceConfig(prev => ({ ...prev, notify_on_execution: checked }))}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={saveRebalanceConfig}
                  disabled={saving}
                  className="flex-1 bg-violet-500 hover:bg-violet-600 text-white rounded-sm"
                >
                  {saving ? "Saving..." : "Save Schedule"}
                </Button>
                <Button
                  onClick={executeRebalanceNow}
                  variant="outline"
                  className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Run Now
                </Button>
              </div>

              {/* Status Indicator */}
              <div className={`p-3 rounded-sm ${rebalanceStatus?.is_running ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-zinc-800/50 border border-white/5'}`}>
                <div className="flex items-center gap-2">
                  {rebalanceStatus?.is_running ? (
                    <>
                      <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                      <span className="text-emerald-400 text-sm">Scheduled rebalancing active</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-zinc-500 rounded-full"></div>
                      <span className="text-zinc-500 text-sm">Scheduled rebalancing inactive</span>
                    </>
                  )}
                </div>
              </div>

              {/* Recent History */}
              {rebalanceHistory.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-zinc-500 mb-2">Recent Executions</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {rebalanceHistory.slice(0, 5).map((h, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-black/30 rounded">
                        <span className="text-zinc-400">{new Date(h.timestamp).toLocaleString()}</span>
                        <span className={h.status === 'completed' ? 'text-emerald-400' : 'text-red-400'}>
                          {h.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Notifications Card */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Bell className="w-5 h-5 text-amber-400" />
                Email Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Enable Notifications</Label>
                  <p className="text-xs text-zinc-500">Receive email alerts for important events</p>
                </div>
                <Switch
                  checked={notifSettings.enabled}
                  onCheckedChange={(checked) => setNotifSettings(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Email Address</Label>
                <Input
                  type="email"
                  value={notifSettings.email_address}
                  onChange={(e) => setNotifSettings(prev => ({ ...prev, email_address: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-black/50 border-white/10 rounded-sm"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Trade Execution Alerts</Label>
                  <Switch
                    checked={notifSettings.trade_alerts}
                    onCheckedChange={(checked) => setNotifSettings(prev => ({ ...prev, trade_alerts: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Price Movement Alerts</Label>
                  <Switch
                    checked={notifSettings.price_alerts}
                    onCheckedChange={(checked) => setNotifSettings(prev => ({ ...prev, price_alerts: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-300">Rebalancing Alerts</Label>
                  <Switch
                    checked={notifSettings.rebalance_alerts}
                    onCheckedChange={(checked) => setNotifSettings(prev => ({ ...prev, rebalance_alerts: checked }))}
                  />
                </div>
              </div>

              {notifSettings.price_alerts && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Price Alert Threshold (%)</Label>
                  <Input
                    type="number"
                    value={notifSettings.price_threshold_pct}
                    onChange={(e) => setNotifSettings(prev => ({ ...prev, price_threshold_pct: parseFloat(e.target.value) || 5 }))}
                    className="bg-black/50 border-white/10 rounded-sm"
                    step="0.5"
                  />
                  <p className="text-xs text-zinc-500">Alert when price moves more than this percentage</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={saveNotificationSettings}
                  disabled={saving}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                >
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
                <Button
                  onClick={sendTestNotification}
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 rounded-sm"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Test
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Export Card */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Download className="w-5 h-5 text-cyan-400" />
                Bulk Report Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-zinc-400 text-sm">
                Download all your real estate reports in a single ZIP file, including:
              </p>
              <ul className="text-sm text-zinc-500 space-y-1 ml-4">
                <li>• Portfolio summary</li>
                <li>• Individual property details</li>
                <li>• Ledger transactions</li>
                <li>• Mortgage payment history</li>
                <li>• P&L reports for each property</li>
              </ul>
              
              <Button
                onClick={downloadBulkReports}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Download All Reports (ZIP)
              </Button>
            </CardContent>
          </Card>

          {/* Research History Card */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Home className="w-5 h-5 text-emerald-400" />
                Real Estate Research History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {researchHistory.length === 0 ? (
                <div className="text-center py-6 text-zinc-500">
                  <Home className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No research history yet</p>
                  <p className="text-xs mt-1">Use the Real Estate AI bots to analyze properties</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {researchHistory.map((r, i) => (
                    <div key={i} className="p-3 bg-black/30 border border-white/5 rounded-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          r.type === 'house_flip' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {r.type === 'house_flip' ? 'Flip Analysis' : 'Rent Analysis'}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {new Date(r.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-white text-sm truncate">{r.address}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
